import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AcademicCapIcon,
  ChartBarIcon,
  TrophyIcon,
  UserGroupIcon,
  StarIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  PlayIcon,
  FireIcon,
  BuildingOfficeIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CollegeBadge from './CollegeBadge.js';

// CSS for Personalization Engine
const personalizationStyles = `
  .flow-node {
    position: relative;
    z-index: 1;
    border-radius: 0.75rem;
    border: 1px solid #e5e7eb;
    background-color: white;
    padding: 1rem;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    transition: all 0.2s;
  }
  .flow-node .flow-icon {
    display: inline-flex;
    height: 2.25rem;
    width: 2.25rem;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
  }
  .flow-node .flow-title { 
    font-weight: 600;
    color: #111827;
  }
  .flow-node .flow-desc { 
    font-size: 0.875rem;
    color: #6b7280;
  }

  /* Active highlight (cycled by JS) */
  .flow-node.is-active { 
    box-shadow: 0 0 0 2px #60a5fa, 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  .flow-node.is-key { 
    background: linear-gradient(135deg, #dbeafe 0%, #ffffff 100%);
  }

  /* Mobile arrows */
  .flow-arrow {
    position: relative;
    margin: 0 auto;
    height: 1.5rem;
    width: 1px;
    background: linear-gradient(to bottom, transparent 0%, #d1d5db 50%, transparent 100%);
  }

  /* Respect reduced motion */
  @media (prefers-reduced-motion: reduce) {
    .animate-pulse, .animate-[flowDash] { 
      animation: none !important; 
    }
  }

  @keyframes flowDash { 
    to { 
      stroke-dashoffset: -56; 
    } 
  }

  /* Mobile specific styles */
  .flow-vert {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  .flow-vert .flow-node {
    width: 100%;
    max-width: 320px;
    margin-bottom: 0.5rem;
  }
  
  .flow-vert .flow-arrow {
    margin: 0.5rem auto;
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('LandingPage error boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-800 font-semibold mb-2">Something went wrong.</p>
            <p className="text-gray-600 text-sm">Please refresh the page or try again.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const LandingPageInner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [billingMode, setBillingMode] = useState('monthly'); // 'monthly' | 'tillexam'
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const appScreenshots = [
    { src: '/dashboard_preview.png', alt: 'Dashboard - Today\'s Plan', title: 'Dashboard' },
    { src: '/practice_test.png', alt: 'Practice Questions', title: 'Practice' },
    { src: '/mock_test_config.png', alt: 'Mock Test Configuration', title: 'Mock Tests' },
    { src: '/mock_test_question.png', alt: 'Mock Test Question', title: 'Test Taking' },
    { src: '/percentile_predictor.png', alt: 'Score Predictor', title: 'Score Predictor' },
    { src: '/analytics.png', alt: 'Analytics Dashboard', title: 'Analytics' }
  ];

  // Redirect logged-in users to dashboard (Netflix-style)
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Image carousel timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        (prevIndex + 1) % appScreenshots.length
      );
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [appScreenshots.length]);

  // Personalization Engine animation
  useEffect(() => {
    const section = document.getElementById('personalization-engine');
    if (!section) return;

    const svg = section.querySelector('#flow-lines');
    const gridRoot = section.querySelector('.pe-desktop'); // desktop container

    const nodes = {
      profile: gridRoot?.querySelector('[data-step="1"]'),
      srs: gridRoot?.querySelector('[data-step="2"]'),
      sess: gridRoot?.querySelector('[data-step="3"]'),
      today: gridRoot?.querySelector('[data-step="4"]'),
      mocks: gridRoot?.querySelector('[data-step="5"]'),
      insights: gridRoot?.querySelector('[data-step="6"]'),
    };

    function pt(el) {
      if (!el || !svg) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      const s = svg.getBoundingClientRect();
      return { x: r.left + r.width/2 - s.left, y: r.top + r.height/2 - s.top };
    }

    function linePath(a, b, curve=0.25) {
      const dx = b.x - a.x, dy = b.y - a.y;
      const cx1 = a.x + dx * curve, cy1 = a.y + dy * curve;
      const cx2 = b.x - dx * curve, cy2 = b.y - dy * curve;
      return `M ${a.x} ${a.y} C ${cx1} ${cy1}, ${cx2} ${cy2}, ${b.x} ${b.y}`;
    }

    // Manhattan (orthogonal) connector path with 90-degree angles
    function orthoPath(leftPt, rightPt) {
      const midX = (leftPt.x + rightPt.x) / 2;
      return `M ${leftPt.x} ${leftPt.y} L ${midX} ${leftPt.y} L ${midX} ${rightPt.y} L ${rightPt.x} ${rightPt.y}`;
    }

    // Anchor to the nearest side of the source/target boxes so lines avoid text
    function anchorPointFor(el, other) {
      if (!el || !other || !svg) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      const o = other.getBoundingClientRect();
      const s = svg.getBoundingClientRect();
      const centerA = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
      const centerB = { x: o.left + o.width / 2, y: o.top + o.height / 2 };
      const dx = centerB.x - centerA.x;
      const dy = centerB.y - centerA.y;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);

      let x; let y; let side;
      if (absDx > absDy) {
        // Connect horizontally: use right/left midpoints
        if (dx >= 0) {
          x = r.right - s.left; // right edge
          side = 'right';
        } else {
          x = r.left - s.left; // left edge
          side = 'left';
        }
        y = centerA.y - s.top;
      } else {
        // Connect vertically: use bottom/top midpoints
        x = centerA.x - s.left;
        if (dy >= 0) {
          y = r.bottom - s.top; // bottom edge
          side = 'bottom';
        } else {
          y = r.top - s.top; // top edge
          side = 'top';
        }
      }
      return { x, y, side };
    }

    function pathBetween(elA, elB) {
      // revert to center-to-center for cleaner aesthetics
      const a = pt(elA);
      const b = pt(elB);
      const d = linePath(a, b, 0.3);
      return { d, a, b };
    }

    function draw() {
      if (!svg || !gridRoot || window.innerWidth < 768) return; // mobile uses vertical flow, no SVG
      
      // Clear existing paths
      svg.innerHTML = '';
      // Sync viewBox to actual pixel size so coordinates match
      const w = svg.clientWidth || svg.parentElement?.clientWidth || 0;
      const h = svg.clientHeight || svg.parentElement?.clientHeight || 0;
      if (w && h) {
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        svg.setAttribute('preserveAspectRatio', 'none');
      }

      // Validate at least one node exists
      const rectCheck = nodes.profile?.getBoundingClientRect();
      if (!rectCheck || !rectCheck.width) return;

      // For stacked layout, draw simple vertical connections
      const P = nodes.profile;
      const S = nodes.srs;
      const SB = nodes.sess;
      const T = nodes.today;
      const M = nodes.mocks;
      const I = nodes.insights;

      // Simple vertical connections between stacked boxes
      const centerX = (el) => {
        const r = el.getBoundingClientRect();
        const s = svg.getBoundingClientRect();
        return r.left - s.left + r.width / 2;
      };
      const bottomY = (el) => {
        const r = el.getBoundingClientRect();
        const s = svg.getBoundingClientRect();
        return r.bottom - s.top;
      };
      const topY = (el) => {
        const r = el.getBoundingClientRect();
        const s = svg.getBoundingClientRect();
        return r.top - s.top;
      };

      const connections = [
        { from: { x: centerX(P), y: bottomY(P) }, to: { x: centerX(S), y: topY(S) } },   // Profile -> SRS
        { from: { x: centerX(S), y: bottomY(S) }, to: { x: centerX(SB), y: topY(SB) } }, // SRS -> SessionBuilder
        { from: { x: centerX(SB), y: bottomY(SB) }, to: { x: centerX(T), y: topY(T) } }, // SessionBuilder -> Today
        { from: { x: centerX(T), y: bottomY(T) }, to: { x: centerX(M), y: topY(M) } },   // Today -> Mocks
        { from: { x: centerX(M), y: bottomY(M) }, to: { x: centerX(I), y: topY(I) } },   // Mocks -> Insights
      ];

      connections.forEach(({ from, to }) => {
        const path = document.createElementNS('http://www.w3.org/2000/svg','path');
        path.setAttribute('d', `M ${from.x} ${from.y} L ${to.x} ${to.y}`);
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', '#94a3b8');
        path.setAttribute('stroke-width', '2');
        path.setAttribute('stroke-linecap', 'round');
        svg.appendChild(path);
      });
    }

    // Cycle active node highlight (desktop & mobile)
    const cycleNodes = section.querySelectorAll('[data-step]');
    let idx = 0;
    let intervalId = null;
    
    function tick() {
      cycleNodes.forEach(n => n.classList.remove('is-active'));
      cycleNodes[idx % cycleNodes.length].classList.add('is-active');
      idx++;
    }

    // Animate on view
    const io = new IntersectionObserver((entries)=>{
      if (!entries[0].isIntersecting) return;
      
      // Start the animation cycle
      tick();
      intervalId = setInterval(tick, 1600);
      
      // Draw simple vertical connections on desktop
      if (window.innerWidth >= 768) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => requestAnimationFrame(draw));
        setTimeout(draw, 100);
        setTimeout(draw, 300);
        setTimeout(draw, 500);
        let resizeTimeout;
        const onResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(draw, 120);
        };
        window.addEventListener('resize', onResize);
        // Save reference for cleanup
        section.__onResize = onResize;
      }
      
      // stop observing once initialized
      io.disconnect();
    }, {threshold: 0.2});
    
    io.observe(section);

    return () => {
      io.disconnect();
      if (intervalId) clearInterval(intervalId);
      if (section.__onResize) {
        window.removeEventListener('resize', section.__onResize);
        delete section.__onResize;
      }
    };
  }, []);

  // Inject CSS styles for Personalization Engine
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = personalizationStyles;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Show loading while redirecting logged-in users
  if (user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  const features = [
    {
      icon: AcademicCapIcon,
      title: "AI-Powered Learning",
      description: "Personalized study plans and recommendations tailored to your CET preparation",
      color: "text-blue-600"
    },
    {
      icon: ChartBarIcon,
      title: "CET Score Predictor",
      description: "Predict your CET rank based on current performance and track improvement",
      color: "text-green-600"
    },
    {
      icon: TrophyIcon,
      title: "Gamified Experience",
      description: "XP, achievements, streaks, and social competition to keep you motivated",
      color: "text-yellow-600"
    },
    {
      icon: BuildingOfficeIcon,
      title: "College Predictor",
      description: "See which colleges you can get with your score and admission guidance",
      color: "text-purple-600"
    },
    {
      icon: UserGroupIcon,
      title: "Maharashtra Community",
      description: "Connect with fellow CET aspirants from across Maharashtra",
      color: "text-red-600"
    },
    {
      icon: FireIcon,
      title: "Previous Year Questions",
      description: "Practice with authentic MH-CET questions from previous years",
      color: "text-orange-600"
    }
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Mumbai",
      company: "VJTI",
      rating: 5,
      text: "W3Skills helped me improve my rank from 25,000 to 8,000. The AI recommendations were spot on!",
      avatar: "/Priya_Sharma.png"
    },
    {
      name: "Rahul Patil",
      location: "Pune",
      company: "COEP",
      rating: 5,
      text: "The gamification kept me motivated throughout my preparation. Got into my dream college!",
      avatar: "/Rahul_Patil.png"
    },
    {
      name: "Sneha Desai",
      location: "Nagpur",
      company: "SPIT",
      rating: 5,
      text: "The previous year questions and mock tests were exactly what I needed. Highly recommended!",
      avatar: "/Sneha_Desai.png"
    }
  ];

  const pricingPlans = {
    monthly: [
    {
      name: "Free",
      price: "₹0",
      period: "forever",
        description: "Diagnostic + sample access",
      features: [
          "Diagnostic + sample chapter",
          "1 sectional mock",
          "Basic analytics",
          "College predictor (manual input)"
      ],
      cta: "Start Free",
      popular: false
    },
    {
      name: "Premium",
      price: "₹499",
      period: "per month",
        description: "Complete PCM prep with SRS + mocks",
      features: [
          "All PCM content + PYQs (2013–2024)",
          "Full & sectional mocks",
          "SRS reviews + SessionBuilder",
          "Detailed analytics & weak-area plan",
          "Score & College predictors (progress-aware)",
          "Smart reminders, streaks, XP"
      ],
      cta: "Start Premium",
      popular: true
      }
    ],
    tillexam: [
      {
        name: "Premium",
        price: "₹2999",
        period: "till exam",
        description: "Everything you need until MHT-CET 2025",
      features: [
          "All PCM content + PYQs (2013–2024)",
          "Full & sectional mocks",
          "SRS reviews + SessionBuilder",
          "Detailed analytics & weak-area plan",
          "Score & College predictors (progress-aware)",
          "Smart reminders, streaks, XP"
        ],
        cta: "Go Premium",
        popular: true
      }
    ]
  };

  // Sticky mobile CTA + event hooks
  const track = (eventName, params = {}) => {
    try {
      // placeholder for GA4
      console.log('analytics', eventName, params);
    } catch {}
  };

  const stats = [
    { number: "25,000+", label: "Maharashtra Students" },
    { number: "4.8/5", label: "Star Rating" },
    { number: "1,500+", label: "Student Reviews" },
    { number: "95%", label: "Success Rate" }
  ];

  const colleges = [
    { acronym: "VJTI", name: "VJTI" },
    { acronym: "COEP", name: "COEP" },
    { acronym: "SPIT", name: "SPIT" },
    { acronym: "PICT", name: "PICT" },
    { acronym: "VIT", name: "VIT Pune" },
    { acronym: "DY", name: "DY Patil" }
  ];

  const scrollToId = (id) => {
    try {
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('scrollIntoView failed:', e);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">W3Skills</h1>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <button
                  onClick={() => scrollToId('features')}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToId('pricing')}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Pricing
                </button>
                <button
                  onClick={() => scrollToId('testimonials')}
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Reviews
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700"
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-3">
              MHT-CET <span className="font-semibold">PCM</span> • Maharashtra State Board aligned • Mobile-first
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Crack MHT-CET (PCM) with a Plan That Adapts to You
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Personalized daily study plan for Physics, Chemistry & Math. CET-level mocks and PYQs, spaced-repetition reviews, and score/college predictors—built only for PCM.
            </p>
            
            {/* Social Proof */}
            <div className="flex items-center justify-center space-x-6 mb-8">
              <div className="flex items-center space-x-1">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <span className="text-gray-600 ml-2">4.8/5 from 1,500+ PCM students • No card needed • Works on mobile</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleGetStarted}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                Take a Free 10-min PCM Diagnostic
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => scrollToId('pricing')}
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                See Plans
              </button>
            </div>
            {/* Mobile App Preview Carousel */}
            <div className="mt-10">
              <div className="max-w-sm mx-auto">
                <div className="bg-white rounded-3xl shadow-2xl p-2 border-8 border-gray-900">
                  <div className="bg-gray-900 rounded-t-2xl p-3 flex items-center justify-center">
                    <div className="w-16 h-1 bg-gray-600 rounded-full"></div>
                  </div>
                  <div className="p-4 relative">
                    <img
                      src={appScreenshots[currentImageIndex].src}
                      alt={appScreenshots[currentImageIndex].alt}
                      className="w-full h-auto rounded-xl transition-opacity duration-500"
                      loading="lazy"
                    />
                    {/* Image title overlay */}
                    <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded text-center">
                      {appScreenshots[currentImageIndex].title}
                    </div>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <p className="text-sm text-gray-600">See how it looks on your phone</p>
                  {/* Carousel indicators */}
                  <div className="flex justify-center space-x-1 mt-3">
                    {appScreenshots.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof band */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3">
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">PYQs covered: 2013–2024</span>
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">Quick notes & formulas: Physics • Chemistry • Math</span>
            <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">Low-data mode: Yes</span>
          </div>
        </div>
      </section>

      {/* How it works (3 steps) */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">How it works</h2>
          <ol className="space-y-3 text-gray-700">
            <li>1. <span className="font-semibold">Sign in</span> with Google or Phone OTP</li>
            <li>2. <span className="font-semibold">2-min profile</span> (XI/XII status, strengths, exam date)</li>
            <li>3. <span className="font-semibold">Get your PCM plan</span> and start today’s session</li>
          </ol>
          <p className="text-sm text-gray-500 mt-3">No app required. Study on your phone.</p>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose W3Skills?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to excel in MH-CET, powered by AI and designed specifically for Maharashtra students.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mb-6`}>
                  <feature.icon className={`h-6 w-6 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Students Say
            </h2>
            <p className="text-xl text-gray-600">
              Real success stories from Maharashtra students who achieved their dream colleges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-lg">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                    <div className="text-sm text-blue-600">{testimonial.company}</div>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 italic">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personalization Engine */}
      <section id="personalization-engine" className="relative mx-auto max-w-6xl px-4 sm:px-6 py-12 pb-20 bg-gray-50">
        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
            Personalization Engine
          </h2>
          <p className="mt-2 text-gray-600">
            How your plan adapts: profile ➝ reviews & smart sessions ➝ daily plan ➝ mocks ➝ insights ➝ next week.
          </p>
        </div>

        {/* Flow container */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6">
          {/* Desktop stacked layout */}
          <div className="hidden md:block relative pe-desktop">
            {/* Row 1: Profile (center) */}
            <div className="max-w-xl mx-auto space-y-6">
              <article className="flow-node" data-step="1" aria-label="Profile">
                <div className="flow-icon bg-blue-50 text-blue-600">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21a8 8 0 10-16 0"></path><circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3 className="flow-title">Profile</h3>
                <p className="flow-desc">Subjects, strengths, exam date, targets.</p>
              </article>
              {/* Row 2: SRS */}
              <article className="flow-node" data-step="2" aria-label="Spaced Repetition">
                <div className="flow-icon bg-emerald-50 text-emerald-600">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 12a9 9 0 119 9M3 12H1m2-7l2 2M12 3v2m7-2l-2 2"></path>
                    <path d="M12 8v5l3 2"></path>
                  </svg>
                </div>
                <h3 className="flow-title">SRS Reviews</h3>
                <p className="flow-desc">Auto-scheduled from your mistakes & recency.</p>
              </article>

              {/* Row 3: SessionBuilder */}
              <article className="flow-node" data-step="3" aria-label="SessionBuilder">
                <div className="flow-icon bg-violet-50 text-violet-600">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 21v-7m0-4V3m8 18v-9m0-4V3m8 18v-5m0-4V3"></path>
                  </svg>
                </div>
                <h3 className="flow-title">SessionBuilder</h3>
                <p className="flow-desc">Blends new + review by accuracy & speed.</p>
              </article>

              {/* Row 4: Today's Plan */}
              <article className="flow-node is-key" data-step="4" aria-label="Today's Plan">
                <div className="flow-icon bg-blue-600 text-white ring-2 ring-blue-100">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
                    <path d="M8 5v14l11-7z"></path>
                  </svg>
                </div>
                <h3 className="flow-title">Today's Plan</h3>
                <p className="flow-desc">Mix of SRS reviews, new concept, timed practice.</p>
              </article>

              {/* Row 5: Mocks */}
              <article className="flow-node" data-step="5" aria-label="Mocks">
                <div className="flow-icon bg-amber-50 text-amber-600">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 8v4l3 3"></path><circle cx="12" cy="13" r="8"></circle>
                    <path d="M10 2h4M19 4l-1 1"></path>
                  </svg>
                </div>
                <h3 className="flow-title">Mocks</h3>
                <p className="flow-desc">Full/sectional CET-level tests.</p>
              </article>

              {/* Row 6: Insights */}
              <article className="flow-node" data-step="6" aria-label="Insights">
                <div className="flow-icon bg-indigo-50 text-indigo-600">
                  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 3v18h18"></path><path d="M7 13l3 3 7-7"></path>
                  </svg>
                </div>
                <h3 className="flow-title">Insights</h3>
                <p className="flow-desc">Time ↔ accuracy, weak areas, next week plan.</p>
              </article>
            </div>

            {/* Connectors (desktop) */}
            <div className="pointer-events-none absolute inset-0">
              <svg id="flow-lines" className="w-full h-full" aria-hidden="true">
                {/* Lines are updated by JS for nicer animation on load/resize */}
              </svg>
            </div>
          </div>

                     {/* Mobile: vertical flow */}
           <div className="md:hidden space-y-3">
             <div className="flow-vert max-w-sm mx-auto">
               <article className="flow-node" data-step="1" aria-label="Profile">
                 <div className="flow-icon bg-blue-50 text-blue-600">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M20 21a8 8 0 10-16 0"></path><circle cx="12" cy="7" r="4"></circle>
                   </svg>
                 </div>
                 <h3 className="flow-title">Profile</h3>
                 <p className="flow-desc">Subjects, strengths, exam date, targets.</p>
               </article>

               <article className="flow-node" data-step="2" aria-label="SRS Reviews">
                 <div className="flow-icon bg-emerald-50 text-emerald-600">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M3 12a9 9 0 119 9M3 12H1m2-7l2 2M12 3v2m7-2l-2 2"></path>
                     <path d="M12 8v5l3 2"></path>
                   </svg>
                 </div>
                 <h3 className="flow-title">SRS Reviews</h3>
                 <p className="flow-desc">Auto-scheduled reviews.</p>
               </article>

               <article className="flow-node" data-step="3" aria-label="SessionBuilder">
                 <div className="flow-icon bg-violet-50 text-violet-600">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M4 21v-7m0-4V3m8 18v-9m0-4V3m8 18v-5m0-4V3"></path>
                   </svg>
                 </div>
                 <h3 className="flow-title">SessionBuilder</h3>
                 <p className="flow-desc">Mixes new + review by performance.</p>
               </article>

               <article className="flow-node is-key" data-step="4" aria-label="Today's Plan">
                 <div className="flow-icon bg-blue-600 text-white ring-2 ring-blue-100">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                 </div>
                 <h3 className="flow-title">Today's Plan</h3>
                 <p className="flow-desc">Do today's tasks to keep your streak.</p>
               </article>

               <article className="flow-node" data-step="5" aria-label="Mocks">
                 <div className="flow-icon bg-amber-50 text-amber-600">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M12 8v4l3 3"></path><circle cx="12" cy="13" r="8"></circle>
                     <path d="M10 2h4M19 4l-1 1"></path>
                   </svg>
                 </div>
                 <h3 className="flow-title">Mocks</h3>
                 <p className="flow-desc">Timed CET-style tests.</p>
               </article>

               <article className="flow-node" data-step="6" aria-label="Insights">
                 <div className="flow-icon bg-indigo-50 text-indigo-600">
                   <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                     <path d="M3 3v18h18"></path><path d="M7 13l3 3 7-7"></path>
                   </svg>
                 </div>
                 <h3 className="flow-title">Insights</h3>
                 <p className="flow-desc">Adjusts your next 7 days automatically.</p>
               </article>
             </div>
           </div>
        </div>
      </section>

      {/* Predictors (transparent & compliant) */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Know where you stand—before the exam</h2>
          <p className="text-gray-700 max-w-3xl">
            Percentile estimates use your <span className="font-semibold">recent mocks</span>. Accuracy improves with more PCM attempts of CET-level difficulty.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate('/cet-score-predictor')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
            >
              Try Score Predictor
            </button>
            <button
              onClick={() => navigate('/college-predictor')}
              className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50"
            >
              Estimate Colleges
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">Typical error ±2–3 percentile when ≥3 recent CET-level mocks are available.</p>
        </div>
      </section>

             {/* Outcomes & college guidance */}
       <section className="py-16 bg-gray-50">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">Outcomes & college guidance</h2>
                       <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-3 lg:grid-cols-6 md:overflow-x-visible">
              {colleges.map((college) => (
                <div key={college.acronym} className="flex-shrink-0 snap-start md:flex-shrink">
                  <CollegeBadge
                    acronym={college.acronym}
                    name={college.name}
                    year="2024"
                  />
                </div>
              ))}
            </div>
           <div className="mt-6">
             <button
               onClick={() => navigate('/college-predictor')}
               className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700"
             >
               Check my fit with College Predictor
             </button>
           </div>
         </div>
       </section>

      {/* Social proof (CET-specific) */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">What PCM students say</h2>
          <div className="flex gap-4 overflow-x-auto snap-x pb-3">
            {[
              { name: 'Aarav P.', year: '2024', percentile: '98.3', branch: 'CS', college: 'VJTI', line: 'SRS + mocks fixed my careless errors in Physics.' },
              { name: 'Riya S.', year: '2025', percentile: '97.2', branch: 'IT', college: 'COEP', line: 'Daily plan kept me consistent on weak topics.' },
              { name: 'Om K.', year: '2024', percentile: '96.8', branch: 'ENTC', college: 'SPIT', line: 'Predictor matched my final percentile within 2 points.' }
            ].map((t) => (
              <div key={t.name} className="min-w-[260px] snap-start bg-gray-50 rounded-lg p-4 shadow">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-gray-900">{t.name}</div>
                  <span className="text-[11px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full">Verified</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">{t.year} • {t.percentile} percentile • {t.branch} • {t.college}</div>
                <p className="text-gray-700 mt-3">“{t.line}”</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">Verification: scorecards collected post‑result; identities anonymized.</p>
        </div>
      </section>

      {/* Pricing Section with toggle */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h2>
            <p className="text-xl text-gray-600">
              Start free and upgrade as you progress. No hidden fees, cancel anytime.
            </p>
            <div className="mt-6 inline-flex items-center bg-white border rounded-lg p-1">
              <button
                onClick={() => setBillingMode('monthly')}
                className={`px-4 py-2 text-sm rounded-md ${billingMode==='monthly' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
              >Monthly</button>
              <button
                onClick={() => setBillingMode('tillexam')}
                className={`px-4 py-2 text-sm rounded-md ${billingMode==='tillexam' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
              >Till-Exam</button>
            </div>
          </div>

                                <div className="flex flex-col md:flex-row justify-center items-stretch gap-8 max-w-4xl mx-auto">
             {pricingPlans[billingMode].map((plan, index) => (
              <div
                key={index}
                 className={`bg-white rounded-lg shadow-lg p-8 w-full max-w-sm flex flex-col ${
                  plan.popular ? 'ring-2 ring-blue-600 relative' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {plan.price}
                    <span className="text-lg text-gray-600 font-normal">/{plan.period}</span>
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </div>

                 <ul className="space-y-4 mb-8 flex-grow">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={handleGetStarted}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </button>
                <div className="text-xs text-gray-500 mt-3 text-center">Cancel anytime • 7-day refund on first purchase • UPI supported</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA band */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to start your PCM plan today?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">Take the diagnostic or explore a sample chapter.</p>
          <button
            onClick={handleGetStarted}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Take the Free 10-min PCM Diagnostic
          </button>
          <div className="mt-3">
            <button
              onClick={() => navigate('/practice')}
              className="text-white/90 underline text-sm"
            >
              Explore a Sample Chapter
            </button>
          </div>
        </div>
      </section>

      {/* Sticky CTA (mobile) */}
      <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-white/95 border-t p-3 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-2">
          <button
            onClick={() => { track('cta_click', { place: 'sticky' }); handleGetStarted(); }}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold shadow"
          >
            Take the Free 10-min PCM Diagnostic
          </button>
        </div>
      </div>

      {/* FAQ (PCM-specific) */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">FAQ</h2>
          <div className="divide-y bg-gray-50 rounded-lg">
            {[
              { q: 'Is this only for PCM?', a: 'Yes—this product is only for PCM (Physics, Chemistry, Math). PCB is not included yet.' },
              { q: 'How is difficulty calibrated vs JEE?', a: 'CET-level, aligned to the Maharashtra State Board syllabus and format.' },
              { q: 'Do I need an app?', a: 'No—mobile web works great. No installs required.' },
              { q: 'Marathi/English content?', a: 'Specify availability as per your content pipeline.' },
              { q: 'How accurate are predictors?', a: 'Accuracy improves with recent mocks; typical error ±2–3 percentile when ≥3 CET-level mocks are available.' },
              { q: 'Refund/cancel policy?', a: 'Cancel anytime. 7-day refund on first purchase (see Terms).' },
              { q: 'Data privacy?', a: 'We use Firebase Auth; see Privacy Policy for details.' }
            ].map((item, idx) => (
              <div key={idx} className="p-4">
                <div className="font-semibold text-gray-900">{item.q}</div>
                <div className="text-gray-700 mt-1 text-sm">{item.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">W3Skills</h3>
              <p className="text-gray-400">
                The #1 choice for MH-CET preparation in Maharashtra.
              </p>
            </div>
                         <div>
               <h4 className="font-semibold mb-4">Features</h4>
               <ul className="space-y-2 text-gray-400">
                 <li><button onClick={() => navigate('/ai-powered-learning')} className="hover:text-white transition-colors">AI-Powered Learning</button></li>
                 <li><button onClick={() => navigate('/cet-score-predictor')} className="hover:text-white transition-colors">CET Score Predictor</button></li>
                 <li><button onClick={() => navigate('/college-predictor')} className="hover:text-white transition-colors">College Predictor</button></li>
                 <li><button onClick={() => navigate('/mock-tests')} className="hover:text-white transition-colors">Mock Tests</button></li>
               </ul>
             </div>
             <div>
               <h4 className="font-semibold mb-4">Support</h4>
               <ul className="space-y-2 text-gray-400">
                 <li><button onClick={() => navigate('/help-center')} className="hover:text-white transition-colors">Help Center</button></li>
                 <li><button onClick={() => navigate('/contact-us')} className="hover:text-white transition-colors">Contact Us</button></li>
                 <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                 <li><button onClick={() => navigate('/terms-of-service')} className="hover:text-white transition-colors">Terms of Service</button></li>
               </ul>
             </div>
             <div>
               <h4 className="font-semibold mb-4">Contact</h4>
               <ul className="space-y-2 text-gray-400">
                 <li>Email: w3skills.devstudio@gmail.com</li>
                 <li>Response Time: 24 hours</li>
                 <li>Service Hours: Mon-Fri 9AM-6PM IST</li>
               </ul>
             </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 W3Skills. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const LandingPage = () => (
  <ErrorBoundary>
    <LandingPageInner />
  </ErrorBoundary>
);

export default LandingPage;
